import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import { BreakPoints, Button, DialogAlert, Flex, Spacer, Text, useBreakpoint } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { RouterService } from '@cortex-shim/router.service';
import { signal } from '@preact/signals-react';
import { ControllerProtocol, Injectable, NewContainer, useResolve, useResolveController } from '@visiba/instantiation';
import { clamp, useMotionValueEvent, useScroll } from 'framer-motion';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { StepBack } from './step_back.component';

function TopBarRoot(props: React.PropsWithChildren) {
	return <NewContainer>{props.children}</NewContainer>;
}

interface TopBarContentProps {
	title?: string;
	goBack?: boolean | string;
	onAbort?: (() => void) | null;
	goBackIntercept?: GoBackInterceptHandler;
	skipAbortDialog?: boolean;
	abortText?: string;
}

function TopBarContent(props: TopBarContentProps) {
	const controller = useResolveController(TopBarController);
	useEffect(() => {
		const item = controller.addItem(
			props.title,
			props?.goBack ?? false,
			props.onAbort,
			props.goBackIntercept,
			props.skipAbortDialog,
			props.abortText,
		);

		return () => {
			controller.removeItem(item);
		};
	}, [controller, props.goBack, props.title, props.onAbort, props.goBackIntercept, props.skipAbortDialog, props.abortText]);

	return null;
}

function GoBackTitle(value: boolean | string | null): string {
	if (value == null) return '';

	if (typeof value === 'string') return value;

	return I18nAndL10n.translate('Component.TopBar.BackText');
}

const StyledTopBarSize = styled.div`
	height: ${tokens.size.x7};
	flex-shrink: 0;
`;

const StyledHeader = styled.div`
	display: flex;
	height: ${tokens.size.x7};
	padding: 0 ${tokens.spacing.x2};
	position: fixed;
	align-items: center;
	justify-content: center;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1;

	&:after {
		content: '';
		position: absolute;
		inset: 0;
		z-index: -1;

		--extended-by: 100px;
		bottom: calc(-1 * var(--extended-by));
		top: -1px;

		--cutoff: calc(100% - var(--extended-by));

		mask-image: linear-gradient(to bottom, black 0, black var(--cutoff), transparent var(--cutoff));
		-webkit-mask-image: linear-gradient(to bottom, black 0, black var(--cutoff), transparent var(--cutoff));

		--blur: 15px;
		backdrop-filter: blur(var(--blur));
		-webkit-backdrop-filter: blur(var(--blur));

		background: linear-gradient(180deg, var(--bg-page-color) 10%, transparent 100%);
		background: linear-gradient(180deg, #fefefe 10%, transparent 100%);

		user-select: none;
		pointer-events: none;
	}

	&:before {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: 1px;
		opacity: var(--_opacity, 0);
		background: rgba(71, 51, 51, 0.06);

		${tokens.breakpointQuery.large} {
			opacity: 1;
		}
	}

	${tokens.breakpointQuery.small} {
		position: absolute;

		&:after {
			display: none;
		}

		&:before {
			display: none;
		}
	}
`;

const StyledAbortSection = styled.span`
	* {
		word-break: keep-all;
	}
`;

function TopBarOutlet(_: React.PropsWithChildren) {
	const routerService = useResolve(RouterService);
	const controller = useResolveController(TopBarController);
	const item = controller.stack.value[controller.stack.value.length - 1] as StackItem | undefined;
	const isMobile = useBreakpoint(BreakPoints.Small);
	const analyticService = useResolve(AnalyticService);

	const ref = useRef<HTMLDivElement>(null);
	const { scrollY } = useScroll();
	useMotionValueEvent(scrollY, 'change', (latest) => {
		ref.current?.style.setProperty('--_opacity', clamp(0, latest / 100, 1).toString());
	});

	function handleGoBackClick() {
		analyticService.trackEvent(AnalyticCategories.TopBar, 'Clicked', 'Go back button');

		if (item == null) return;

		const event = item.event;
		item.goBackIntercept?.(event);

		if (event.defaultPrevented) return;

		routerService.back();
	}

	return (
		<>
			<StyledTopBarSize />
			<StyledHeader ref={ref}>
				<Flex direction='row' grow='1' justify='start' basis='15%' shrink={0}>
					{item?.goBack ? <StepBack onClick={handleGoBackClick} text={GoBackTitle(item.goBack)} /> : null}
				</Flex>

				<Flex direction='row' grow='2' align='center' justify='center' basis='70%' asChild>
					<Spacer horizontal={tokens.spacing.x1}>
						<Text.p variant={isMobile ? 'labelSmallRegular' : 'labelSemiBold'} style={{ textAlign: 'center' }}>
							{item?.title}
						</Text.p>
					</Spacer>
				</Flex>

				<Flex direction='row' grow='1' justify='end' basis='15%' shrink={0} asChild>
					<StyledAbortSection>
						{item?.onAbort != null && !item?.skipAbortDialog ? (
							<DialogAlert
								trigger={
									<Button variant='ghost' size='xSmall'>
										{withTranslation('Component.TopBar.AbortText')}
									</Button>
								}
								header={withTranslation('Component.TopBar.AbortDialog.Header')}
								description={withTranslation('Component.TopBar.AbortDialog.Description')}
								abortText={withTranslation('Component.TopBar.AbortDialog.AbortText')}
								successText={withTranslation('Component.TopBar.AbortDialog.SuccessText')}
								onSuccess={item.onAbort}
							/>
						) : null}
						{item?.onAbort != null && item?.skipAbortDialog ? (
							<Button variant='ghost' size='xSmall' onClick={item.onAbort}>
								{item?.abortText ?? withTranslation('Component.TopBar.AbortText')}
							</Button>
						) : null}
					</StyledAbortSection>
				</Flex>
			</StyledHeader>
		</>
	);
}

type OnAbortCallback = (event: React.MouseEvent<HTMLButtonElement>) => void;
type GoBackInterceptHandler = (event: Event) => void;

interface StackItem {
	event: Event;
	title?: string;
	onAbort: OnAbortCallback | null;
	goBack: boolean | string;
	goBackIntercept?: GoBackInterceptHandler;
	skipAbortDialog: boolean;
	abortText?: string;
}

@Injectable()
class TopBarController implements ControllerProtocol {
	public stack = signal<StackItem[]>([]);

	constructor() {
		// Empty
	}

	whenMount() {
		// Empty
	}

	whenDemount() {
		// Empty
	}

	public addItem(
		title?: string,
		goBack?: boolean | string,
		onAbort?: (() => void) | null,
		goBackIntercept?: GoBackInterceptHandler,
		skipAbortDialog = false,
		abortText?: string,
	): StackItem {
		const list = this.stack.peek();
		const prevItem = list[list.length - 1];
		const newTile: string | undefined = title ?? prevItem?.title;

		const item: StackItem = {
			event: new Event('goBack', { cancelable: true }),
			title: newTile,
			goBack: goBack ?? false,
			goBackIntercept: goBackIntercept,
			onAbort: onAbort !== undefined ? onAbort : prevItem?.onAbort,
			skipAbortDialog,
			abortText,
		};

		this.stack.value = [...list, item];

		return item;
	}

	public removeItem(item: StackItem) {
		const list = this.stack.peek();
		const index = list.indexOf(item);

		if (index <= -1) return;

		list.splice(index, 1);
		this.stack.value = [...list];
	}

	public pop() {
		const list = this.stack.peek();
		list.pop();

		this.stack.value = [...list];
	}
}

export const TopBar = {
	Root: TopBarRoot,
	Outlet: TopBarOutlet,
	Content: TopBarContent,
} as const;
